import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@shared/constants";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {
  MapPin, ShoppingCart, Users, BookOpen, BarChart3, Building2,
  Bot, Menu, X, User, LogOut, Heart, Package, Crown, ChevronDown, Calendar, Music2, Shirt
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import CannabisLeafIcon from "@/components/CannabisLeafIcon";
import { readGuestMarketplaceCart } from "@/lib/guestMarketplaceCart";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { href: "/dispensaries", label: "Dispensaries", icon: MapPin },
  { href: "/strains", label: "Strains", icon: null },
  { href: "/shop", label: "Shop", icon: ShoppingCart },
  { href: "/supply", label: "Supply", icon: Shirt },
  { href: "/community", label: "Community", icon: Users },
  { href: "/learn", label: "Learn", icon: BookOpen },
  { href: "/music", label: "Music", icon: Music2 },
  { href: "/budtender", label: "Budtender", icon: Bot },
  { href: "/about", label: "About", icon: Building2 },
  { href: "/pricing", label: "For Business", icon: BarChart3 },
];

export default function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cart item count
  const { data: cartItems } = trpc.cart.list.useQuery(undefined, { enabled: isAuthenticated, staleTime: 0, refetchOnWindowFocus: true, refetchOnMount: true, refetchInterval: false });
  const cartCount = (cartItems || []).reduce((sum: number, item: any) => sum + (item.cartItem?.quantity || item.quantity || 1), 0);
  const [guestCartCount, setGuestCartCount] = useState(0);

  useEffect(() => {
    const refreshGuestCartCount = () => setGuestCartCount(readGuestMarketplaceCart().reduce((sum, item) => sum + item.quantity, 0));
    refreshGuestCartCount();
    window.addEventListener("releafa:guest-cart-changed", refreshGuestCartCount);
    window.addEventListener("storage", refreshGuestCartCount);
    return () => {
      window.removeEventListener("releafa:guest-cart-changed", refreshGuestCartCount);
      window.removeEventListener("storage", refreshGuestCartCount);
    };
  }, [isAuthenticated]);
  const visibleCartCount = isAuthenticated ? cartCount : guestCartCount;

  // Auto-close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Check if user owns a dispensary
  const dispensaryQuery = trpc.dispensaryOwner.getMyDispensary.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const ownsDispensary = !!dispensaryQuery.data;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 shrink-0">
          <img src={IMAGES.logo} alt="" className="h-9 w-9 rounded-lg" width={36} height={36} loading="eager" />
          <span className="text-lg sm:text-xl font-bold tracking-tight text-gradient-main">
            RELEAFA <span className="text-emerald-400">NETWORK</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map(({ href, label, icon: Icon }) => {
            const active = location === href || location.startsWith(href + "/");
            return (
              <Link key={href} href={href}>
                <span
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    active
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >
                  {Icon ? <Icon className="w-4 h-4" /> : <CannabisLeafIcon className="w-4 h-4" />}
                  {label}
                </span>
              </Link>
            );
          })}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Cart Icon */}
          <Link href="/cart">
            <Button variant="ghost" size="icon" className="relative" aria-label={isAuthenticated ? "Cart" : "Guest cart"}>
              <ShoppingCart className="w-5 h-5" />
              {visibleCartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-md leading-none">
                  {visibleCartCount > 99 ? '99+' : visibleCartCount}
                </span>
              )}
            </Button>
          </Link>
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <span className="hidden md:inline text-sm font-medium">{user.name || "User"}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border" onCloseAutoFocus={() => setMobileOpen(false)}>
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-sm font-semibold">{user.name || "User"}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <Link href="/profile">
                  <DropdownMenuItem><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                </Link>
                <Link href="/orders">
                  <DropdownMenuItem><Package className="w-4 h-4 mr-2" />Orders</DropdownMenuItem>
                </Link>
                <Link href="/strains">
                  <DropdownMenuItem><Heart className="w-4 h-4 mr-2" />Favorites</DropdownMenuItem>
                </Link>
                <Link href="/vip">
                  <DropdownMenuItem><Crown className="w-4 h-4 mr-2 text-gold" />VIP Membership</DropdownMenuItem>
                </Link>
                {ownsDispensary && (
                  <Link href="/dispensary/dashboard">
                    <DropdownMenuItem><Building2 className="w-4 h-4 mr-2 text-primary" />My Dispensary</DropdownMenuItem>
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link href="/admin">
                    <DropdownMenuItem><BarChart3 className="w-4 h-4 mr-2" />Admin Dashboard</DropdownMenuItem>
                  </Link>
                )}
                {((user as any).isVip || user.role === "admin") && (
                  <Link href="/lounge">
                    <DropdownMenuItem><Crown className="w-4 h-4 mr-2 text-gold" />RELEAFA Lounge</DropdownMenuItem>
                  </Link>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              className="bg-primary text-primary-foreground hover:bg-emerald-light font-semibold text-sm"
              onClick={() => { window.location.href = getLoginUrl(); }}
            >
              Sign In
            </Button>
          )}

          {/* Mobile menu toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden overflow-hidden border-t border-border/50 glass"
          >
            <div className="container py-3 flex flex-col gap-1">
              {/* VIP CTA - standout gold */}
              <Link href="/vip" onClick={() => setMobileOpen(false)}>
                <span className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-bold transition-colors bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30">
                  <Crown className="w-4 h-4" />
                  Free VIP for Your First Year
                </span>
              </Link>

              <Link href="/events" onClick={() => setMobileOpen(false)}>
                <span className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${location === "/events" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}>
                  <Calendar className="w-4 h-4" />
                  Private Events
                </span>
              </Link>

              {navLinks.map(({ href, label, icon: Icon }) => {
                const active = location === href;
                const isSupply = href === "/supply";
                return (
                  <Link key={href} href={href} onClick={() => setMobileOpen(false)}>
                    <span
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isSupply
                          ? "text-gold hover:text-gold-light hover:bg-gold/10"
                          : active
                          ? "bg-primary/15 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      }`}
                    >
                      {Icon ? <Icon className="w-4 h-4" /> : <CannabisLeafIcon className="w-4 h-4" />}
                      {label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
